import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Typography,
  Stack,
  Divider,
  Dialog,
  Button,
  IconButton,
  TextField,
  Paper,
  Autocomplete,
  Alert,
  GlobalStyles,
} from "@mui/material";
import { v4 as uuid } from 'uuid';
import PrintIcon from "@mui/icons-material/Print";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const toInputDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const fromInputDate = (value) => {
  if (!value) return null;
  const [y, m, d] = value.split("-");
  return new Date(Number(y), Number(m) - 1, Number(d));
};

const hkdFromCents = (cents) => Number(cents || 0) / 100;

// --- MOVED OUTSIDE ---
const LabelRow = ({ label, value, field, type = "text", isEdit, form, setForm }) => {
  if (!isEdit) {
    return (
      <Typography variant="body2">
        <span style={{ fontWeight: "bold", display: "inline-block", width: 80 }}>{label}:</span> {value}
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
       <Typography variant="body2" sx={{ fontWeight: "bold", width: 80 }}>{label}:</Typography>
       <TextField
          size="small"
          type={type}
          inputProps={{ 'aria-label': `Session ${label.toLowerCase()}` }}
          value={form[field]}
          onChange={(e) => {
            const val = e.target.value;
            setForm((p) => ({ ...p, [field]: val }));
          }}
          sx={{ backgroundColor: "#F3F4F6", borderRadius: 2, flexGrow: 1 }}
        />
    </Box>
  );
};
// ---------------------

const InvoiceReceiptDialog = ({
  open,
  onClose,
  docType,
  selected,
  doctorProfile,
  diagnosisList,
  formatMoney,
  getBillingStatus,
  formatDate,
  formatShortDate,
  durationMinutes,
  documentRecord = null,
  onIssue,
  onReplace,
  historyControls,
}) => {
  const [isEdit, setIsEdit] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const beforeEdit = useRef(null);
  const originalSnapshot = useRef(null);
  const frozen = Boolean(documentRecord) && !replacing;
  const [receiptPaymentMethod, setReceiptPaymentMethod] = useState(() => documentRecord?.snapshot?.paymentMethod || '');
  const receiptPaymentMethodBeforeEdit = useRef("");
  const [receiptPaymentStatus, setReceiptPaymentStatus] = useState(() => documentRecord?.snapshot?.paymentStatus || '');
  const receiptPaymentStatusBeforeEdit = useRef("");
  const initialForm = () => documentRecord?.snapshot?.form || {
    billToName: selected?.patient_name || 'Client',
    caseId: selected?.id || '—',
    sessionDate: toInputDate(selected?.appointment_starttime),
    sessionType: 'First Session',
    durationText: durationMinutes(selected),
    diagnosisText: (diagnosisList || []).join(', '),
    items: [{ id: 'item-1', description: selected?.category || 'Session', qty: 1, rateCents: Number(selected?.payment_total_amount_cents || 0) }],
  };
  const [form, setForm] = useState(initialForm);
  const storedSnapshot = documentRecord?.snapshot;
  const issuer = storedSnapshot?.issuer || doctorProfile;

  // These are document labels only, never financial updates.
  useEffect(() => {
    setReceiptPaymentMethod(storedSnapshot?.paymentMethod || "");
    receiptPaymentMethodBeforeEdit.current = "";
    setReceiptPaymentStatus(storedSnapshot?.paymentStatus || "");
    receiptPaymentStatusBeforeEdit.current = "";
  }, [open, selected?.id, docType, storedSnapshot]);

  // Initialize form when dialog opens or selected item changes
  useEffect(() => {
    if (!open || !selected) return;

    setForm(initialForm());

    setIsEdit(false);
    setReplacing(false);
    setReviewing(false);
    setReason('');
    setSaveError('');
    // Reset only when the selected document changes, not during an edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selected?.id, docType, storedSnapshot]);

  // Update diagnosis text when it loads, but only if we are basically clean
  useEffect(() => {
    if (open && !storedSnapshot && !isEdit && !replacing && diagnosisList?.length > 0) {
      setForm((prev) => ({
        ...prev,
        diagnosisText: diagnosisList.join(", "),
      }));
    }
  }, [diagnosisList, open, storedSnapshot, isEdit, replacing]);

  const subtotalCents = useMemo(() => {
    return form.items.reduce((sum, it) => {
      const qty = Number(it.qty || 0);
      const rate = Number(it.rateCents || 0);
      return sum + qty * rate;
    }, 0);
  }, [form.items]);

  // Platform fee removed from logic
  const totalCents = subtotalCents;

  const handleItemChange = (id, key, value) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((it) =>
        it.id === id ? { ...it, [key]: value } : it
      ),
    }));
  };

  const handleAddItem = () => {
    setForm((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          id: uuid(),
          description: "",
          qty: 1,
          rateCents: 0,
        },
      ],
    }));
  };

  const handleRemoveItem = (id) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.filter((it) => it.id !== id),
    }));
  };

  const handleSave = () => {
    setIsEdit(false);
  };

  const handleCancelEdit = () => {
    if (!selected) return;
    if (beforeEdit.current) {
      setForm(beforeEdit.current.form);
      setReceiptPaymentMethod(beforeEdit.current.paymentMethod);
      setReceiptPaymentStatus(beforeEdit.current.paymentStatus);
      setIsEdit(false);
      setReplacing(false);
      setReviewing(false);
      setReason('');
      setSaveError('');
      return;
    }
    setReceiptPaymentMethod(receiptPaymentMethodBeforeEdit.current);
    setReceiptPaymentStatus(receiptPaymentStatusBeforeEdit.current);
    setForm({
      billToName: selected?.patient_name || "Client",
      caseId: selected?.id || "—",
      sessionDate: toInputDate(selected?.appointment_starttime),
      sessionType: "First Session",
      durationText: durationMinutes(selected),
      diagnosisText: (diagnosisList || []).join(", "),
      items: [
        {
          id: "item-1",
          description: selected?.category || "Session",
          qty: 1,
          rateCents: Number(selected?.payment_total_amount_cents || 0),
        },
      ],
    });
    setIsEdit(false);
  };

  const handlePrint = () => window.print();
  const snapshot = () => ({
    form,
    issuer: Object.fromEntries(Object.entries(issuer || {}).map(([key, value]) => [key, String(value ?? '')])),
    paymentMethod: receiptPaymentMethod,
    paymentStatus: docType === 'Receipt' ? receiptPaymentStatus : (storedSnapshot?.paymentStatus ?? getBillingStatus(selected)),
    legacyNumber: storedSnapshot?.legacyNumber ?? String(selected?.stripe_event_id || ''),
    legacyDate: storedSnapshot?.legacyDate ?? String(selected?.created_at || ''),
  });
  const rememberEdit = () => {
    beforeEdit.current = snapshot();
    receiptPaymentMethodBeforeEdit.current = receiptPaymentMethod;
    receiptPaymentStatusBeforeEdit.current = receiptPaymentStatus;
    setIsEdit(true);
  };
  const validate = () => {
    if (!form.billToName.trim() || !/^\d{4}-\d{2}-\d{2}$/.test(form.sessionDate)) return 'Enter a client name and valid session date.';
    if (!form.items.length || form.items.length > 50 || form.items.some(item => !item.description.trim() || !Number.isFinite(Number(item.qty)) || Number(item.qty) <= 0 || Number(item.qty) > 10000 || !Number.isSafeInteger(Number(item.rateCents)) || Number(item.rateCents) < 0 || Number(item.rateCents) > 100000000000)) return 'Check the descriptions, quantities and amounts of the document line items.';
    return '';
  };
  const persist = async (replace) => {
    const invalid = validate();
    if (invalid) { setSaveError(invalid); return; }
    setSaving(true);
    setSaveError('');
    try {
      if (replace) await onReplace({ snapshot: snapshot(), originalSnapshot: originalSnapshot.current, reason: reason.trim() });
      else await onIssue(snapshot());
      setReplacing(false);
      setReviewing(false);
      setIsEdit(false);
    } catch {
      setSaveError('Document not saved. Your original remains valid unless a previous request succeeded. Retry or close and reload the history to check.');
    } finally { setSaving(false); }
  };

  return (
    <Dialog aria-labelledby="billing-document-title" className="billing-document-modal" open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      {open && <GlobalStyles styles={{ '@media print': {
        'body': { overflow: 'visible !important', padding: '0 !important' },
        'body > *:not(.billing-document-modal)': { display: 'none !important' },
        '.billing-document-modal': { position: 'static !important' },
        '.billing-document-modal .MuiBackdrop-root': { display: 'none' },
        '.billing-document-modal .MuiDialog-container': { display: 'block', height: 'auto' },
        '.billing-document-modal .MuiDialog-paper': { maxHeight: 'none', maxWidth: 'none', width: '100%', margin: 0, overflow: 'visible', boxShadow: 'none' },
      } }} />}
      {/* Top Bar */}
      <Box sx={{ p: 2, display: "flex", flexWrap: 'wrap', gap: 1, justifyContent: "space-between", alignItems: "center", '@media print': { display: 'none' } }}>
        <Typography id="billing-document-title" variant="h6">{docType} document</Typography>
        <Stack direction="row" gap={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          {!isEdit && !replacing && (
            <>
              {!frozen && <>
              <Button
                startIcon={<EditIcon />}
                variant="outlined"
                onClick={rememberEdit}
                disabled={saving}
                sx={{ color: "#000", borderColor: "#E0E0E0", textTransform: "none", fontWeight: "bold" }}
              >
                Edit
              </Button>
              {onIssue && <Button onClick={() => persist(false)} disabled={saving}>Save issued copy</Button>}
              </>}
              {onReplace && documentRecord?.status !== 'void' && <Button disabled={saving} onClick={() => {
                originalSnapshot.current = snapshot();
                rememberEdit();
                setReplacing(true);
              }}>Void and replace</Button>}
              <Button
                startIcon={<PrintIcon />}
                variant="outlined"
                onClick={handlePrint}
                disabled={saving}
                sx={{ color: "#000", borderColor: "#E0E0E0", textTransform: "none", fontWeight: "bold" }}
              >
                Print
              </Button>
            </>
          )}

          {isEdit && !replacing && (
            <>
              <Button
                startIcon={<CloseIcon />}
                variant="outlined"
                onClick={handleCancelEdit}
                sx={{ color: "#000", borderColor: "#E0E0E0", textTransform: "none", fontWeight: "bold" }}
              >
                Cancel
              </Button>
              <Button
                startIcon={<SaveIcon />}
                variant="contained"
                onClick={handleSave}
                sx={{ textTransform: "none", fontWeight: "bold" }}
              >
                Save
              </Button>
            </>
          )}

          <IconButton onClick={onClose} disabled={saving} aria-label="Close document">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <Divider />

      <Box sx={{ px: 3, '@media print': { display: 'none' } }}>
        {typeof historyControls === 'function' ? historyControls(saving) : historyControls}
        {!documentRecord && onIssue && <Alert severity="info" sx={{ my: 1 }}>Edits are temporary until you save an issued copy. Older printed copies were not stored; verify these details against your original before replacing it.</Alert>}
        {replacing && <Box sx={{ py: 2 }}>
          <Typography variant="h6">{reviewing ? 'Review replacement' : 'Prepare replacement'}</Typography>
          <Typography>This changes document validity only. It does not refund payments, cancel appointments, restore credits or change the payment record.</Typography>
          <TextField fullWidth label="Reason for replacement" multiline inputProps={{ maxLength: 1000 }} value={reason} disabled={reviewing || saving} onChange={e => setReason(e.target.value)} sx={{ my: 2 }} />
          <Stack direction="row" gap={1} sx={{ flexWrap: 'wrap' }}>
            <Button disabled={saving} onClick={handleCancelEdit}>Cancel replacement</Button>
            {reviewing ? <>
              <Button disabled={saving} onClick={() => { setReviewing(false); setIsEdit(true); }}>Back to editing</Button>
              <Button variant="contained" disabled={saving} onClick={() => persist(true)}>Confirm void and issue replacement</Button>
            </> : <Button variant="contained" onClick={() => {
              const invalid = !reason.trim() ? 'Enter a reason for replacement.' : validate();
              setSaveError(invalid);
              if (!invalid) { setReviewing(true); setIsEdit(false); }
            }}>Review replacement</Button>}
          </Stack>
        </Box>}
        {saveError && <Alert severity="error">{saveError}</Alert>}
      </Box>

      <Box sx={{ p: 3, backgroundColor: "#FFFFFF" }}>
        {documentRecord?.status === 'void' && <Box sx={{ border: '2px solid #b91c1c', p: 2, mb: 2 }}>
          <Typography variant="h4" color="error">VOID</Typography>
          <Typography>Reason: {documentRecord.void_reason}</Typography>
          <Typography>Voided by {documentRecord.voided_by} on {formatDate(documentRecord.voided_at)}</Typography>
          <Typography>Replacement: {documentRecord.replacement_id}</Typography>
        </Box>}
        {documentRecord?.replaces_id && <Typography>Replaces document: {documentRecord.replaces_id}</Typography>}
        {documentRecord?.legacy_capture && <Typography>Legacy copy captured for correction. Original reference: {storedSnapshot?.legacyNumber || 'Not available'}. Original document date: {formatDate(storedSnapshot?.legacyDate)}. Earlier printed edits cannot be recovered.</Typography>}
        {replacing && <Typography variant="h5">REPLACEMENT DRAFT</Typography>}
        {/* Header */}
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
            <Typography variant="h6" sx={{ color: "#0E7C7B", fontWeight: 700, fontSize: 30 }}>
              {issuer?.name || "—"}
            </Typography>
            {issuer?.practice_title && (
              <Typography variant="body2" sx={{ color: "#1F2937", fontWeight: 600 }}>
                {issuer.practice_title}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              Address: {issuer?.address || "—"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Phone: {issuer?.phone_number || "—"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Email: {issuer?.secondary_email || "—"}
            </Typography>
          </Box>

          <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, maxWidth: '100%', overflowWrap: 'anywhere' }}>
            <Box
              sx={{
                display: "inline-block",
                px: 2.5,
                py: 0.8,
                borderRadius: 2,
                backgroundColor: "#ECFDF5",
                color: "#0E7C7B",
                fontWeight: 800,
                letterSpacing: 0.5,
                mb: 1,
              }}
            >
              {docType.toUpperCase()}
            </Box>
            <Typography variant="body2" color="text.secondary">
              Number: {replacing ? 'Assigned on confirmation' : documentRecord?.number || selected?.stripe_event_id || "—"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Date: {formatDate(documentRecord?.legacy_capture ? storedSnapshot?.legacyDate : documentRecord?.created_at || selected?.created_at)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Case ID: {selected?.id || "—"}
            </Typography>
          </Box>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Bill To + Session Details */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={0}
          sx={{ width: "100%" }}
        >
          <Box sx={{ width: { xs: '100%', sm: '48%' } }}>
            <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700, fontSize: 16 }}>
              BILL TO:
            </Typography>

            <Stack spacing={1} sx={{ mt: 0.5 }}>
              {!isEdit ? (
                <Typography sx={{ fontWeight: 600 }}>{form.billToName}</Typography>
              ) : (
                <TextField
                  size="small"
                  value={form.billToName}
                  inputProps={{ 'aria-label': 'Bill to name', maxLength: 4000 }}
                  onChange={(e) => setForm((p) => ({ ...p, billToName: e.target.value }))}
                  sx={{ backgroundColor: "#F3F4F6", borderRadius: 2 }}
                />
              )}
              <Typography variant="caption" color="text.secondary">
                Case: {form.caseId || "—"}
              </Typography>
            </Stack>
          </Box>

          <Box sx={{ width: { xs: '100%', sm: '48%' }, textAlign: { xs: 'left', sm: 'right' } }}>
            <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700, fontSize: 16 }}>
              SESSION DETAILS:
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <LabelRow 
                label="Date" 
                value={formatShortDate(fromInputDate(form.sessionDate))}
                field="sessionDate"
                type="date"
                isEdit={isEdit}
                form={form}
                setForm={setForm}
              />
              <LabelRow 
                label="Type" 
                value={form.sessionType} 
                field="sessionType"
                isEdit={isEdit}
                form={form}
                setForm={setForm}
              />
              <LabelRow 
                label="Duration" 
                value={form.durationText} 
                field="durationText"
                isEdit={isEdit}
                form={form}
                setForm={setForm}
              />
            </Box>
          </Box>
        </Stack>

        {/* Diagnosis */}
        <Box sx={{ mt: 2, p: 2, borderRadius: 2, border: "1px solid #E2E8F0", backgroundColor: "#F0F7FF" }}>
          <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700 }}>
            DIAGNOSIS:
          </Typography>

          {!isEdit ? (
            <Typography variant="body2">{form.diagnosisText || "—"}</Typography>
          ) : (
            <TextField
              fullWidth
              multiline
              minRows={2}
              value={form.diagnosisText}
              inputProps={{ 'aria-label': 'Diagnosis', maxLength: 4000 }}
              onChange={(e) => setForm((p) => ({ ...p, diagnosisText: e.target.value }))}
              sx={{ mt: 1, backgroundColor: "#FFFFFF", borderRadius: 2 }}
            />
          )}
        </Box>

        {/* Services */}
        <Box role="region" aria-label="Document services" tabIndex={0} sx={{ mt: 3, overflowX: 'auto', '@media print': { overflow: 'visible' } }}>
          <Box sx={{ minWidth: isEdit ? 560 : 360, '@media print': { minWidth: 0 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700 }}>
              SERVICES:
            </Typography>
            {isEdit && (
              <Button startIcon={<AddIcon />} variant="outlined" onClick={handleAddItem} sx={{ textTransform: "none", borderRadius: 2 }}>
                Add Item
              </Button>
            )}
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" sx={{ color: "#64748B", fontWeight: 700 }}>
            <Box sx={{ flex: 5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                DESCRIPTION
              </Typography>
            </Box>
            <Box sx={{ flex: 1, textAlign: "center" }}>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                QTY
              </Typography>
            </Box>
            <Box sx={{ flex: 2, textAlign: "right" }}>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                RATE
              </Typography>
            </Box>
            <Box sx={{ flex: 2, textAlign: "right" }}>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                AMOUNT
              </Typography>
            </Box>
            {isEdit && <Box sx={{ width: 40 }} />}
          </Stack>

          <Divider sx={{ my: 1 }} />

          {form.items.map((item) => (
            <Stack key={item.id} direction="row" alignItems="center" sx={{ py: 1 }}>
              <Box sx={{ flex: 5 }}>
                {!isEdit ? (
                  <Typography variant="body2">{item.description || "—"}</Typography>
                ) : (
                  <TextField
                    size="small"
                    value={item.description}
                    inputProps={{ 'aria-label': 'Service description', maxLength: 2000 }}
                    onChange={(e) => handleItemChange(item.id, "description", e.target.value)}
                    sx={{ backgroundColor: "#F3F4F6", borderRadius: 2, width: "95%" }}
                  />
                )}
              </Box>
              <Box sx={{ flex: 1, textAlign: "center" }}>
                {!isEdit ? (
                  <Typography variant="body2">{item.qty}</Typography>
                ) : (
                  <TextField
                    size="small"
                    type="number"
                    inputProps={{ 'aria-label': 'Quantity', min: 0.01, max: 10000, step: 'any' }}
                    value={item.qty === 0 ? "" : item.qty} 
                    onChange={(e) => {
                        const val = e.target.value;
                        handleItemChange(item.id, "qty", val === "" ? 0 : Number(val));
                    }}
                    sx={{ backgroundColor: "#F3F4F6", borderRadius: 2, width: 80 }}
                  />
                )}
              </Box>
              <Box sx={{ flex: 2, textAlign: "right" }}>
                {!isEdit ? (
                  <Typography variant="body2">{formatMoney(item.rateCents)}</Typography>
                ) : (
                  <TextField
                    size="small"
                    type="number"
                    inputProps={{ 'aria-label': 'Rate', min: 0, step: 0.01 }}
                    value={hkdFromCents(item.rateCents) === 0 ? "" : hkdFromCents(item.rateCents)}
                    onChange={(e) => {
                        const val = e.target.value;
                        const numVal = val === "" ? 0 : Number(val);
                        handleItemChange(item.id, "rateCents", Math.round(numVal * 100));
                    }}
                    sx={{ backgroundColor: "#F3F4F6", borderRadius: 2, width: 120 }}
                  />
                )}
              </Box>
              <Box sx={{ flex: 2, textAlign: "right" }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatMoney(Number(item.qty || 0) * Number(item.rateCents || 0))}
                </Typography>
              </Box>
              {isEdit && (
                <Box sx={{ width: 40, textAlign: "right" }}>
                  <IconButton aria-label="Remove line item" onClick={() => handleRemoveItem(item.id)}>
                    <DeleteOutlineIcon sx={{ color: "#EF4444" }} />
                  </IconButton>
                </Box>
              )}
            </Stack>
          ))}

          <Divider sx={{ my: 2 }} />

          {/* Totals */}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Box sx={{ minWidth: 260 }}>
              {/* <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Subtotal:
                </Typography>
                <Typography variant="body2">{formatMoney(subtotalCents)}</Typography>
              </Stack>
              <Divider sx={{ my: 1 }} /> */}
              <Paper sx={{ p: 1.5, backgroundColor: "#ECFDF5", borderRadius: 2 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Total:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#0E7C7B" }}>
                    {formatMoney(totalCents)}
                  </Typography>
                </Stack>
              </Paper>
            </Box>
          </Box>
          </Box>
        </Box>

        {docType === "Receipt" && (
          <Box sx={{ mt: 3, p: 2, borderRadius: 2, border: "1px solid #E2E8F0", backgroundColor: "#FFFFFF" }}>
            <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700 }}>
              PAYMENT METHOD:
            </Typography>
            {isEdit ? (
              <Autocomplete
                freeSolo
                openOnFocus
                options={["Credits", "Credit / Debit Card", "Prepaid Package"]}
                value={receiptPaymentMethod}
                inputValue={receiptPaymentMethod}
                onChange={(_event, value) => setReceiptPaymentMethod(value || "")}
                onInputChange={(_event, value) => setReceiptPaymentMethod(value)}
                renderInput={(params) => (
                  <TextField {...params} size="small" inputProps={{ ...params.inputProps, "aria-label": "Payment Method" }} />
                )}
                sx={{ mt: 0.5, backgroundColor: "#F3F4F6", borderRadius: 2 }}
              />
            ) : (
              <Typography variant="body2" sx={{ minHeight: "1.5em", overflowWrap: "anywhere" }}>
                {receiptPaymentMethod}
              </Typography>
            )}
          </Box>
        )}

        {/* Client payment choice for receipts; existing billing status for invoices. */}
        <Box sx={{ mt: 3, p: 2, borderRadius: 2, border: "1px solid #E2E8F0", backgroundColor: "#FFFFFF" }}>
          <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700 }}>
            PAYMENT STATUS:
          </Typography>
          {docType === "Receipt" && isEdit ? (
            <Autocomplete
              freeSolo
              openOnFocus
              options={["Awaiting payment", "Paid", "Refunded"]}
              value={receiptPaymentStatus}
              inputValue={receiptPaymentStatus}
              onChange={(_event, value) => setReceiptPaymentStatus(value || "")}
              onInputChange={(_event, value) => setReceiptPaymentStatus(value)}
              renderInput={(params) => (
                <TextField {...params} size="small" inputProps={{ ...params.inputProps, "aria-label": "Payment Status" }} />
              )}
              sx={{ mt: 0.5, backgroundColor: "#F3F4F6", borderRadius: 2 }}
            />
          ) : (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                overflowWrap: "anywhere",
                minHeight: docType === "Receipt" ? "1.5em" : undefined,
                color:
                  (docType === "Receipt" ? receiptPaymentStatus : storedSnapshot?.paymentStatus ?? getBillingStatus(selected)) === "Paid"
                    ? "#16A34A"
                    : (docType === "Receipt" ? receiptPaymentStatus : storedSnapshot?.paymentStatus ?? getBillingStatus(selected)) === "Refunded"
                    ? "#DC2626"
                    : "#F59E0B",
              }}
            >
              {docType === "Receipt" ? receiptPaymentStatus : storedSnapshot?.paymentStatus ?? getBillingStatus(selected)}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Notes */}
        <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700 }}>
          Notes:
        </Typography>
        <Box component="ul" sx={{ mt: 1, pl: 2, color: "#1F2937" }}>
          <li>
            <Typography variant="caption">
              This invoice is for professional psychological services rendered.
            </Typography>
          </li>
          <li>
            <Typography variant="caption">
              Services may be eligible for insurance reimbursement. Please consult with your insurance provider.
            </Typography>
          </li>
          <li>
            <Typography variant="caption">
              For questions regarding this invoice, please contact us at info@tikcare.co
            </Typography>
          </li>
        </Box>
        <Divider sx={{ my: 3 }} />
        <Typography variant="caption" color="text.secondary">
          Generated on {new Date().toLocaleString("en-US")}
        </Typography>
      </Box>
    </Dialog>
  );
};

export default InvoiceReceiptDialog;
